import { useState, useEffect, useCallback } from "react"
import {
  DEFAULT_LOCALE,
  LOCALE_FOLDER_MAP,
  SUPPORTED_LOCALES,
} from "src/constants/locales"

// 缓存翻译文件
const translationsCache = {}
const loadingPromises = {}

export { DEFAULT_LOCALE, SUPPORTED_LOCALES }

/**
 * 规范化语言码；空值 / 非法值回退 DEFAULT_LOCALE（en）。
 */
export const normalizeLocale = (locale) => {
  if (!locale || typeof locale !== "string") return DEFAULT_LOCALE
  const normalized = locale.trim()

  return SUPPORTED_LOCALES.includes(normalized) ? normalized : DEFAULT_LOCALE
}

/**
 * 解析首屏语言优先级：
 * 1) URL ?locale=（外链指定，最高优先）
 * 2) localStorage
 * 3) DEFAULT_LOCALE
 * 同时写回 localStorage，保证后续 changeLocale / 刷新一致。
 */
const resolveInitialLocale = () => {
  const queryLocale = new URLSearchParams(window.location.search).get("locale")
  const locale = normalizeLocale(
    queryLocale || localStorage.getItem("locale") || DEFAULT_LOCALE
  )
  if (localStorage.getItem("locale") !== locale) {
    localStorage.setItem("locale", locale)
  }

  return locale
}

/**
 * 拉取 /public/locales/{folder}/{namespace}.json
 * - UI 码（jp/kr/it-ch/tw）经 LOCALE_FOLDER_MAP 映射到真实目录
 * - 缺文件时回退 en
 */
const loadTranslations = async (locale, namespace = "common") => {
  const safeLocale = normalizeLocale(locale)
  const cacheKey = `${safeLocale}:${namespace}`
  if (translationsCache[cacheKey]) return translationsCache[cacheKey]
  if (loadingPromises[cacheKey]) return loadingPromises[cacheKey]

  const mappedLocale = LOCALE_FOLDER_MAP[safeLocale] || safeLocale

  const fetchJson = async (loc) => {
    try {
      const res = await fetch(`/locales/${loc}/${namespace}.json`)
      if (!res.ok) throw new Error("not found")

      return await res.json()
    } catch {
      return null
    }
  }

  const promise = (async () => {
    let data = await fetchJson(mappedLocale)
    if (!data) {
      console.warn(`[i18n] Missing ${safeLocale}/${namespace}, fallback to ${DEFAULT_LOCALE}`)
      data = (await fetchJson(DEFAULT_LOCALE)) || {}
    }
    translationsCache[cacheKey] = data
    delete loadingPromises[cacheKey]

    return data
  })()

  loadingPromises[cacheKey] = promise

  return promise
}

// hook
export const useTranslation = (namespace = "common") => {
  const [translations, setTranslations] = useState({})
  const [currentLocale, setCurrentLocale] = useState(DEFAULT_LOCALE)
  const [isLoading, setIsLoading] = useState(true)

  const isSSR = typeof window === "undefined"

  // 翻译函数
  const t = useCallback(
    (key, options = {}) => {
      if (isSSR || isLoading) return key // SSR 阶段只返回 key

      let value = translations[key]
      if (!value) {
        // 兼容 a.b.c 嵌套 key
        const keys = key.split(".")
        value = translations
        for (const k of keys) {
          value = value?.[k]
        }
      }

      if (typeof value !== "string") {
        // console.log(111, key)
        return key
      }

      // 简单插值：{{name}} → options.name
      return value.replace(/{{\s*(\w+)\s*}}/g, (_, v) =>
        String(options[v] ?? "")
      )
    }, [isLoading, translations, isSSR]
  )

  // 浏览器端才去加载 JSON
  useEffect(() => {
    if (isSSR) return

    const applyLocale = (locale) => {
      const safeLocale = normalizeLocale(locale)
      setIsLoading(true)
      loadTranslations(safeLocale, namespace).then((data) => {
        setTranslations(data)
        setCurrentLocale(safeLocale)
        setIsLoading(false)
      })
    }

    // URL ?locale= 优先于 localStorage，避免与 _app 切换竞态
    applyLocale(resolveInitialLocale())

    // storage：跨标签页；localeChange：同页 changeLocale 派发
    const handleLocaleChange = () => {
      const newLocale = normalizeLocale(localStorage.getItem("locale") || DEFAULT_LOCALE)
      if (localStorage.getItem("locale") !== newLocale) {
        localStorage.setItem("locale", newLocale)
      }
      applyLocale(newLocale)
    }

    window.addEventListener("storage", handleLocaleChange)
    window.addEventListener("localeChange", handleLocaleChange)

    return () => {
      window.removeEventListener("storage", handleLocaleChange)
      window.removeEventListener("localeChange", handleLocaleChange)
    }
  }, [namespace, isSSR])

  /**
   * 写入 localStorage 并广播 localeChange。
   * 非法语言 normalize 到 en；与当前相同则跳过。
   */
  const changeLocale = useCallback((newLocale) => {
    if (typeof window === "undefined") return

    const locale = normalizeLocale(newLocale)
    if (localStorage.getItem("locale") === locale) return

    localStorage.setItem("locale", locale)
    window.dispatchEvent(new Event("localeChange"))
  }, [])

  return { t, locale: currentLocale, isLoading, changeLocale }
}

// 静态翻译（SSR/构建时使用）
export const getStaticTranslation = async (
  key,
  locale = DEFAULT_LOCALE,
  namespace = "common"
) => {
  const translations = await loadTranslations(locale, namespace)

  if (translations[key]) {
    return translations[key]
  }

  const keys = key.split(".")
  let value = translations
  for (const k of keys) {
    value = value?.[k]
    if (value === undefined) return key
  }

  return typeof value === "string" ? value : key
}

export default useTranslation
